import { deleteUser, getRoleList, getUserList } from "api/userManagement";
import AddUser from "components/Modal/AddUser";
import DeleteUser from "components/Modal/DeleteUser";
import EditUser from "components/Modal/EditUser";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import UserDetails from "components/Modal/UserDetails";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import ButtonComp from "themes/elements/Button";
import UserTable from "themes/elements/Table/UserTable";
import {
  retrievePermissions,
  retrieveUserData,
  storePermissions,
} from "utils/cryptoStorage";
import dateUtils from "utils/dateUtils";
import { isPermit } from "utils/permissionCheck";

import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import {
  setCurrentPageNo,
  setDeleteUserLoading,
  setLoading,
  setRolesData,
  setSelectedUser,
  setTotalPageNo,
  setUserDeleteModalOpen,
} from "./userSlice";

function UserManagement() {
  const userToken = retrieveUserData();
  let admin_user_id, admin_user_name;
  const permissions = retrievePermissions();

  if (userToken) {
    ({ admin_user_id, admin_user_name } = userToken);
  }

  const [details, setDetails] = useState(false);
  const [add, setAdd] = useState(false);
  const [filterTitle, setFilterTitle] = useState("");
  const [filterMail, setFilterMail] = useState("");
  const [filterRole, setFilterRole] = useState();
  const [filterType, setFilterType] = useState({ name: "All", value: 3 });
  const [filterStatus, setFilterStatus] = useState({ value: 2, name: "All" });
  const [filterCreatedDate, setFilterCreatedDate] = useState(null);
  const [editUser, setEditUser] = useState(false);
  const [userData, setUserData] = useState([]);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { selectedUser, isLoading, currentPageNo, totalPageNo } = useSelector(
    (state) => state.users
  );

  const columnData = [
    {
      title: "Name",
      key: "name",
      filter: "searchTitle",
      width: 247,
    },
    {
      title: "Email",
      key: "email",
      filter: "searchMail",
      width: 250,
    },
    {
      title: "Role",
      key: "role_name",
      filter: "selectRole",
      dropDown: ["Super Admin", "General Admin", "Councillor"],
      width: 175,
    },
    {
      title: "TYPE",
      key: "type",
      filter: "selectType",
      placeholder: "User type",
      dropDown: [
        { name: "All", value: 3 },
        { name: "CSD User", value: 1 },
        { name: "Active directory user", value: 2 },
      ],
      chip: true,
      width: 175,
    },
    {
      title: "Created on",
      key: "created_date",
      filter: "datePicker",
      width: 175,
    },
    {
      title: "Status",
      key: "status",
      filter: "selectStatus",
      dropDown: [
        { value: 2, name: "All" },
        { value: 1, name: "Active" },
        { value: 0, name: "Inactive" },
      ],
      chip: true,
      width: 175,
    },
  ];

  useEffect(() => {
    fetchRoles();
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [
    currentPageNo,
    filterTitle,
    filterMail,
    filterRole,
    filterCreatedDate,
    filterStatus,
    filterType,
  ]);

  const fetchUsers = async () => {
    dispatch(setLoading(true));
    try {
      const response = await getUserList({
        status: filterStatus?.value,
        userType: 1,
        roleId: filterRole ? filterRole : -1,
        page: currentPageNo,
        perPage: 10,
        limit: 10,
        name: filterTitle,
        email: filterMail,
        createdDate: dateUtils.formatDateToYYYMMDD(filterCreatedDate),
        userType: filterType?.value,
      });
      if (response && response.data) {
        setUserData(response?.data?.users);
      }
      if (currentPageNo === 1) {
        dispatch(setTotalPageNo(response?.data.meta?.last_page * 10));
      }
      dispatch(setCurrentPageNo(response?.data.meta?.current_page));
    } catch (error) {}
    dispatch(setLoading(false));
  };

  const fetchRoles = async () => {
    try {
      const response = await getRoleList({
        page: 1,
        perPage: 100,
        status: 2,
      });
      if (response && response?.roleList) {
        dispatch(setRolesData(response?.roleList));
      }
    } catch (error) {}
  };

  const deleteUserData = async () => {
    dispatch(setDeleteUserLoading(true));
    try {
      const response = await deleteUser({
        admin_user_id: selectedUser.admin_user_id,
        email: selectedUser.email,
        userType: selectedUser.user_type,
      });
      if (response?.message) {
        toast.success(response.message);
        // Toast("success", response?.message);
        dispatch(setDeleteUserLoading(false));

        fetchUsers();
        dispatch(setUserDeleteModalOpen(false));
      }
    } catch (error) {
      dispatch(setDeleteUserLoading(false));
    } finally {
      dispatch(setDeleteUserLoading(false));
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      const updatedPermissions = retrievePermissions();
      if (JSON.stringify(permissions) !== JSON.stringify(updatedPermissions)) {
        storePermissions(updatedPermissions);
      }
    }, 1000); // Check every second, adjust as needed for performance considerations

    return () => clearInterval(interval);
  }, [permissions]);
  return (
    <>
      <div className="page-wrapper flex flex-column gap-2">
        <div className="flex wrap gap-2 align-center justify-between">
          <h2 className="page-title">User Management</h2>
          <div className="flex align-center gap-4">
            {isPermit(1, "view") && (
              <ButtonComp
                onClick={() => {
                  dispatch(setCurrentPageNo(1));
                  navigate("/user-management/manage-roles");
                }}
                variant="secondary"
              >
                Manage roles
              </ButtonComp>
            )}
            {isPermit(2, "create") && (
              <ButtonComp onClick={() => setAdd(true)} prefix>
                Add user
              </ButtonComp>
            )}
          </div>
        </div>
        {isPermit(2, "view") ? (
          <UserTable
            filterTitle={filterTitle}
            setFilterTitle={setFilterTitle}
            filterMail={filterMail}
            setFilterMail={setFilterMail}
            filterRole={filterRole}
            setFilterRole={setFilterRole}
            filterType={filterType}
            setFilterType={setFilterType}
            filterStatus={filterStatus}
            setFilterStatus={setFilterStatus}
            filterCreatedDate={filterCreatedDate}
            setFilterCreatedDate={setFilterCreatedDate}
            isLoading={isLoading}
            dataClick={(data) => {
              dispatch(setSelectedUser(data));
              setDetails(true);
            }}
            column={columnData}
            data={userData}
            totalPageNo={totalPageNo}
            currentPageNo={currentPageNo}
            editAction
            onDeleteAction={(data) => {
              dispatch(setSelectedUser(data));
              dispatch(setUserDeleteModalOpen(true));
            }}
            handleEdit={(data) => {
              dispatch(setSelectedUser(data));
              setEditUser(true);
            }}
          />
        ) : (
          <div
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              // backgroundColor: "#fff3cd",
              color: "#856404",
              fontFamily: "Arial, sans-serif",
              boxSizing: "border-box",
              padding: "20px",
              textAlign: "center", // Ensures text and icon are centered
            }}
          >
            <div>
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                size="lg"
                style={{ marginRight: "10px", fontSize: "80px" }}
              />
              <p
                style={{
                  marginRight: "10px",
                  fontSize: "21px",
                  marginTop: "10px",
                }}
              >
                You do not have permission to access this module.
              </p>
            </div>
          </div>
        )}
      </div>
      <UserDetails setEdit={setEditUser} open={details} setOpen={setDetails} />
      <AddUser open={add} setOpen={setAdd} fetchUsers={fetchUsers} />
      <EditUser
        open={editUser}
        setOpen={setEditUser}
        data={selectedUser}
        fetchUsers={fetchUsers}
      />
      <DeleteUser
        selectedData={selectedUser}
        data={selectedUser}
        handleDeleteNotification={deleteUserData}
      />
    </>
  );
}

export default UserManagement;
